"use client";

import type { PricingConfiguration } from "@/features/calendar/types/pricing.tsx";

import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { DefaultPricingCard } from "./DefaultPricingCard";
import { SubjectPricingCard } from "./SubjectPricingCard";

import { toast } from "@/utils/customToast";
import { getAllPricingConfigurations } from "@/features/calendar/api/Pricing";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { CoachMarker } from "@/components/CoachMarker";

export const LessonPricingPage = () => {
  const { t } = useTranslation(["calendar", "common", "help"]);
  const [isLoading, setIsLoading] = useState(true);
  const [defaultPricing, setDefaultPricing] = useState<PricingConfiguration | null>(null);
  const [subjectPricings, setSubjectPricings] = useState<PricingConfiguration[]>([]);

  // Fetch pricing configurations
  const fetchPricingConfigurations = async () => {
    try {
      setIsLoading(true);
      const response = await getAllPricingConfigurations();
      const pricingData = response.data.data;

      // Set default pricing
      const defaultPricing = pricingData.find((pricing) => pricing.is_default);

      setDefaultPricing(defaultPricing || null);

      // Set subject-specific pricing
      const subjectPricings = pricingData.filter((pricing) => pricing.is_subject_specific);

      setSubjectPricings(subjectPricings);
    } catch (error) {
      console.error("Failed to fetch pricing configurations:", error);
      toast.error(t("calendar:pricing.failedToLoadPricing"), {
        description: t("calendar:pricing.tryAgainOrContactSupport"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh pricing data after updates
  const refreshPricingData = async () => {
    await fetchPricingConfigurations();
  };

  useEffect(() => {
    fetchPricingConfigurations();
  }, []);

  return (
    <ScrollArea className="h-[calc(100dvh_-_4rem)]">
      <title>{`${t("calendar:pricing.lessonPricing")} - Ximly`}</title>
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="container mx-auto max-w-3xl space-y-4 px-2 py-3 sm:space-y-6 sm:px-4 sm:py-6"
        initial={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-4 sm:mb-6">
          <div>
            <h1 className="text-accent-foreground text-lg font-bold sm:text-xl md:text-2xl">
              {t("calendar:pricing.lessonPricing")}
            </h1>
            <p className="text-muted-foreground mt-1 text-xs sm:text-sm md:text-base">
              {t("calendar:pricing.lessonPricingDescription")}
            </p>
          </div>
        </div>
        <CoachMarker
          description={t("help:coachMarkers.defaultPricing.description", {
            defaultValue:
              "This is your base lesson price that will be used for all subjects unless you set specific pricing for individual subjects. You can adjust this anytime and it will apply to new lesson bookings.",
          })}
          id="default-pricing-card"
          placement="left"
          title={t("help:coachMarkers.defaultPricing.title", {
            defaultValue: "Set Your Default Pricing",
          })}
        >
          <DefaultPricingCard
            defaultPricing={defaultPricing}
            isLoading={isLoading}
            onPricingUpdated={refreshPricingData}
          />
        </CoachMarker>
        <CoachMarker
          description={t("help:coachMarkers.subjectPricing.description", {
            defaultValue:
              "Set different prices for different subjects and curriculum levels. This allows you to charge more for advanced topics or specialized subjects while keeping basic subjects at your default rate.",
          })}
          id="subject-pricing-card"
          placement="left"
          title={t("help:coachMarkers.subjectPricing.title", {
            defaultValue: "Subject-Specific Pricing",
          })}
        >
          <SubjectPricingCard
            isLoading={isLoading}
            subjectPricings={subjectPricings}
            onPricingUpdated={refreshPricingData}
          />
        </CoachMarker>
      </motion.div>
    </ScrollArea>
  );
};
